import type { NubeComponent, NubeComponentStyle } from '@tiendanube/nube-sdk-types';
import { createElement, type CSSProperties, type ReactNode } from 'react';

/**
 * `NubeComponent` tree → React elements, for the Form Designer preview.
 *
 * 🚨 **This is what makes the preview and the storefront structurally identical.** Both consume the
 * SAME tree from `buildCodForm`, so a block can no longer exist on one and not the other, render in
 * a different order, or carry different text. Only the pixel rendering of ~15 primitives can drift,
 * and that surface is this file (`MIGRATION.md` §6.1).
 *
 * Before this, `LivePreview` mounted `@cod/form-renderer` — the legacy DOM renderer — while the
 * storefront rendered from `form-tree`. Nothing kept them in step except hand-porting, and every
 * mismatch had to be found in a screenshot.
 *
 * ## Plain DOM elements, not Nimbus components
 *
 * Nuvemshop's advice is to map onto Nimbus, because native NubeSDK components follow Nimbus tokens.
 * That holds for components rendered with **default** styling. Ours are not: `codStyles()` sets
 * every visual property from the merchant's config, and it is written to be self-contained
 * precisely because there is no cascade to lean on. A Nimbus `Button` brings its own padding and
 * typography, which the `style` prop would then have to out-specify — the exact fight that
 * dominated the storefront port.
 *
 * What the host demonstrably emits is plain elements carrying our style objects: `field` renders
 * `[input, label]` in that order, and `text()` renders paragraph-like (which is why `THEME_RESET`
 * zeroes the paragraph margin). Mirroring that reproduces what we can observe.
 *
 * ## Two things that must NOT be dropped
 *
 *  - **`styled` CSS.** `::placeholder`, `:focus-within` and the offer `@keyframes` live only there.
 *    Dropping it would make the preview disagree with the storefront in exactly the places the
 *    style prop cannot reach. Each node's CSS is injected as a real rule scoped to a generated
 *    class — see `styledRule`.
 *  - **`!important`.** React silently ignores it inside a style object, so it is stripped rather
 *    than left to look like it applies. Nothing in the admin fights us for these properties, so the
 *    plain value is the correct rendering of the merchant's intent.
 */

/** The union of props a built node can carry. The SDK types them per component; we read leniently. */
interface TreeNode {
  type?: string;
  __internalId?: string;
  key?: string | number;
  children?: unknown;
  style?: NubeComponentStyle | Record<string, NubeComponentStyle>;
  styled?: string;
  [prop: string]: unknown;
}

/**
 * The four longhands a shorthand expands to, for the shorthands our styles actually mix.
 *
 * 🚨 React warns — "Removing a style property during rerender (marginTop) when a conflicting
 * property is set (margin)" — whenever a node loses a longhand between renders while the shorthand
 * stays, and it warns because it cannot guarantee the removal takes effect. Our styles mix the two
 * constantly and legitimately: `THEME_RESET` sets `margin: '0'` and is spread first, then
 * individual styles add `marginTop`. That is correct CSS and correct on the storefront, which has
 * no React in it — so the shorthand is expanded HERE, in the React adapter, rather than changing a
 * theme both bundles ship. Seen in the Designer on every mode toggle, since the tree it rebuilds
 * reuses the same DOM nodes with a different set of margins.
 */
const SHORTHANDS: Record<string, readonly string[]> = {
  margin: ['marginTop', 'marginRight', 'marginBottom', 'marginLeft'],
  padding: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'],
};

/** Split `0` / `0 auto` / `1px 2px 3px` / `1px 2px 3px 4px` into its four sides. */
function sides(value: string): [string, string, string, string] | null {
  const parts = value.trim().split(/\s+/);
  if (parts.length === 0 || parts.length > 4) return null;
  // A `var()` or `calc()` with spaces inside would be split wrongly; leave those as a shorthand.
  if (parts.some((p) => /[()]/.test(p))) return null;
  const [a, b = a, c = a, d = b] = parts as [string, string?, string?, string?];
  return [a, b, c, d];
}

/** React drops a style value containing `!important`. Strip it and keep the value. */
function toCss(style: NubeComponentStyle | undefined): CSSProperties | undefined {
  if (!style) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(style)) {
    const clean = typeof value === 'string' ? value.replace(/\s*!important\s*$/, '') : value;
    const longhands = SHORTHANDS[key];
    const split = longhands && typeof clean === 'string' ? sides(clean) : null;
    if (longhands && split) {
      // Assigned unconditionally, which is what CSS does: a later shorthand overrides earlier
      // longhands, and a later longhand overrides the expansion. Skipping already-set keys would
      // have inverted the first case — and that case is the one `THEME_RESET`'s header warns
      // about, so it has to behave.
      longhands.forEach((prop, i) => {
        out[prop] = split[i];
      });
      continue;
    }
    out[key] = clean;
  }
  return out as CSSProperties;
}

/** A stable class name for a node, so its `styled` CSS can be scoped to it. */
function classOf(node: TreeNode): string | undefined {
  if (!node.styled || !node.__internalId) return undefined;
  // `__internalId` is `type-appId-hash`; a CSS class cannot start with a digit, and the app id may
  // contain characters that are illegal in a selector.
  return `cod-${node.__internalId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

/**
 * The node's `styled` CSS, rescoped from `&` to its generated class.
 *
 * The host replaces `&` with whatever selector it generates; here that is the class. `@keyframes`
 * blocks are left alone — they are top-level at-rules with no `&` in them.
 */
export function styledRule(css: string, className: string): string {
  return css.replace(/&/g, `.${className}`);
}

const CHILDREN_KEY = 'children';

function childList(children: unknown): unknown[] {
  if (children === undefined || children === null) return [];
  return Array.isArray(children) ? children : [children];
}

/** Per-part style slots, for the components whose `style` is an object of parts. */
function part(node: TreeNode, name: string): CSSProperties | undefined {
  const style = node.style as Record<string, NubeComponentStyle> | undefined;
  return toCss(style?.[name]);
}

/** SVG attribute names differ from the SDK's camelCase props only in `viewBox`, which matches. */
const SVG_TAG: Record<string, string> = {
  svgRoot: 'svg',
  svgPath: 'path',
  svgCircle: 'circle',
  svgLine: 'line',
  svgRect: 'rect',
  svgPolygon: 'polygon',
  svgPolyline: 'polyline',
  svgEllipse: 'ellipse',
  svgG: 'g',
};

/** Props the walker consumes itself and must never spread onto a DOM element. */
const INTERNAL = new Set([
  'type',
  '__internalId',
  'key',
  'children',
  'style',
  'styled',
  'id',
  'name',
  'label',
  'value',
  'placeholder',
  'options',
  'checked',
  'disabled',
  'href',
  'target',
  'src',
  'alt',
  'mask',
  'maxLength',
  'autoFocus',
  'onChange',
  'onClick',
  'onBlur',
  'onFocus',
  'row',
]);

/** Everything left over — the SVG geometry attributes, chiefly. */
function passthrough(node: TreeNode): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(node)) if (!INTERNAL.has(k)) out[k] = v;
  return out;
}

export interface RenderOptions {
  /** Collected `styled` CSS, in tree order. The caller renders it into one `<style>`. */
  readonly css: string[];
}

function renderNode(node: unknown, key: string, opts: RenderOptions): ReactNode {
  if (node === null || node === undefined || node === false) return null;
  if (typeof node === 'string' || typeof node === 'number') return node;
  if (typeof node !== 'object') return null;

  const n = node as TreeNode;
  const kids = childList(n.children).map((child, i) => renderNode(child, `${key}.${i}`, opts));
  const className = classOf(n);
  if (className && n.styled) opts.css.push(styledRule(n.styled, className));

  // `ariaLabel` is the SDK's prop name; the DOM wants `aria-label`. Dropping it left every
  // glyph-only control in the preview unnamed — the date picker's ‹ › arrows and day cells, and
  // the popup's ×, which is otherwise just the character "×". React omits an undefined attribute,
  // so this is inert on the nodes that carry no label.
  const base = {
    key,
    className,
    'aria-label': n['ariaLabel'] as string | undefined,
    style: toCss(n.style as NubeComponentStyle),
  };

  switch (n.type) {
    // Containers. `col` and `row` are flex in the host; the tree sets `display` where it matters,
    // but the DEFAULT direction is part of the component's identity, not of its style.
    case 'box':
      return createElement('div', base, ...kids);
    case 'col':
      return createElement(
        'div',
        { ...base, style: { display: 'flex', flexDirection: 'column', ...base.style } },
        ...kids,
      );
    case 'row':
      return createElement(
        'div',
        { ...base, style: { display: 'flex', flexDirection: 'row', ...base.style } },
        ...kids,
      );
    case 'fragment':
      return createElement('div', { key, style: { display: 'contents' } }, ...kids);

    // 🚨 A paragraph, not a span. `text()` renders paragraph-like on the host — that is why every
    // style object here carries a margin reset, and a `<span>` would hide those bugs in the preview.
    case 'txt':
      return createElement('p', base, ...kids);

    case 'img':
      return createElement('img', {
        ...base,
        src: n['src'] as string,
        alt: (n['alt'] as string) ?? '',
      });

    case 'link':
      return createElement(
        'a',
        { ...base, href: n['href'] as string, target: n['target'] as string | undefined },
        ...kids,
      );

    case 'button':
      return createElement(
        'button',
        {
          ...base,
          type: 'button' as const,
          disabled: Boolean(n['disabled']),
          onClick: n['onClick'] as (() => void) | undefined,
        },
        ...kids,
      );

    // 🚨 `[input, label]`, in that order — the host renders the label BELOW the control, which is
    // why every field in the tree hides its native label and draws its own above.
    case 'field':
      return createElement(
        'div',
        { key, className, style: part(n, 'container') },
        createElement('input', {
          key: 'input',
          name: n['name'] as string,
          defaultValue: (n['value'] as string) ?? '',
          placeholder: (n['placeholder'] as string) ?? '',
          disabled: Boolean(n['disabled']),
          autoFocus: Boolean(n['autoFocus']),
          style: part(n, 'input'),
          onChange: undefined,
          readOnly: true,
        }),
        createElement('label', { key: 'label', style: part(n, 'label') }, n['label'] as string),
      );

    case 'txtarea':
      return createElement(
        'div',
        { key, className, style: part(n, 'container') },
        createElement('textarea', {
          key: 'input',
          name: n['name'] as string,
          defaultValue: (n['value'] as string) ?? '',
          style: part(n, 'input'),
          readOnly: true,
        }),
        createElement('label', { key: 'label', style: part(n, 'label') }, n['label'] as string),
      );

    case 'select': {
      const options = (n['options'] as Array<{ label: string; value: string }>) ?? [];
      return createElement(
        'div',
        { key, className, style: part(n, 'container') },
        createElement(
          'select',
          {
            key: 'select',
            name: n['name'] as string,
            value: (n['value'] as string) ?? '',
            disabled: Boolean(n['disabled']),
            style: part(n, 'select'),
            onChange: () => undefined,
          },
          options.map((o) => createElement('option', { key: o.value, value: o.value }, o.label)),
        ),
        createElement('label', { key: 'label', style: part(n, 'label') }, n['label'] as string),
      );
    }

    case 'check':
      return createElement(
        'label',
        { key, className, style: part(n, 'container') },
        createElement('input', {
          key: 'box',
          type: 'checkbox',
          name: n['name'] as string,
          checked: Boolean(n['checked']),
          disabled: Boolean(n['disabled']),
          style: part(n, 'checkbox'),
          onChange: () => undefined,
        }),
        createElement('span', { key: 'label', style: part(n, 'label') }, n['label'] as string),
      );

    default: {
      const tag = n.type ? SVG_TAG[n.type] : undefined;
      // SVG primitives — roughly a fifth of the tree (every field icon and the header glyph), and
      // the one group Nimbus has no equivalent for. Geometry props pass straight through.
      if (tag) return createElement(tag, { ...base, ...passthrough(n) }, ...kids);
      // An unknown type renders its children rather than vanishing: a new SDK component should
      // degrade to its contents, not silently delete a block from the preview.
      return kids.length ? createElement('div', { key }, ...kids) : null;
    }
  }
}

/**
 * Render a built tree, plus the `<style>` its `styled()` rules need.
 *
 * Returns both so the caller decides where the stylesheet goes; they must be rendered together or
 * the pseudo-element rules are lost.
 */
export function renderNubeTree(tree: NubeComponent): { node: ReactNode; css: string } {
  const opts: RenderOptions = { css: [] };
  const node = renderNode(tree, 'cod', opts);
  return { node, css: opts.css.join('\n') };
}

export { CHILDREN_KEY };
